import React, { useState, useEffect } from 'react';
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Box, TextField, Button, Stack, useColorScheme, useMediaQuery } from '@mui/material';
import { Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ColorModeSelect from '../../shared-theme/ColorModeSelect';
import logoCabanaWhite from '../../images/logonextcabana-white.svg';
import logoCabanaBlack from '../../images/logonextcabana-dark.svg';
import AppTheme from '../../../frontend/shared-theme/AppTheme';
import { validatePassword } from '../profile/validationUserDates';
import WarningAlert from '../../../frontend/components/WarningAlert';
import PasswordInput from '../../../frontend/components/PasswordInput';

const PasswordContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100dvh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    // Modo claro: gradiente verde bem claro para o fundo off-white
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsla(115, 78%, 91%, 1.00), #fffcf2)',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      // Modo escuro: gradiente verde escuro para o fundo preto
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(121, 73%, 13%, 0.50), #0e0e0e)',
    }),
  },
}));

function PasswordContent() {
  const { mode, systemMode } = useColorScheme();
  const isMobile = useMediaQuery('(max-width: 450px)');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordValidations, setPasswordValidations] = useState({
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    isLengthValid: false,
  });

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(false)
      }, 6000)
    }
  }, [alert])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { validations, errorMessage } = validatePassword(newPassword);
    setPasswordValidations(validations);

    if (newPassword !== confirmPassword) {
      setAlert(true)
      setAlertMessage("As senhas devem ser iguais.")
      return;
    }

    try {
      const auth = getAuth();
      await verifyPasswordResetCode(auth, code);
      await confirmPasswordReset(auth, code, newPassword);
      setSuccess(true);
    } catch (error: any) {
      if (errorMessage) {
        setAlert(true)
        setAlertMessage(errorMessage)
        return;
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setCode(params.get('oobCode') || '');
  }, [location.search]);

  return (
    <>
      <CssBaseline />
      <PasswordContainer direction="column" alignItems="center">
        <Card variant="outlined" sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '100%' : '400px',
          gap: 3,
          mt: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={mode === 'dark' || systemMode === 'dark' ? logoCabanaWhite : logoCabanaBlack}
              alt="logo-cabanalink"
              style={{ width: '300px', height: 'auto' }}
            />
          </div>
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ width: '100%', fontSize: 'clamp(1rem, 10vw, 1.15rem)' }}
            >
              {success ? 'Senha alterada com sucesso!' : 'Redefinir Senha'}
            </Typography>
            <Typography color='text.secondary'>
              {success
                ? 'Agora faça o login com sua nova senha.'
                : 'A senha deve conter de 6 a 10 caracteres, letra maiúscula e minúscula, e número.'}
            </Typography>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                display: 'flex',
                fontFamily: 'Roboto, sans-serif',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {!success ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      mt: 2
                    }}
                  >
                    <PasswordInput
                      password={newPassword}
                      setPassword={setNewPassword}
                      labelMessage="Nova Senha"
                    />
                    <PasswordInput
                      password={confirmPassword}
                      setPassword={setConfirmPassword}
                      labelMessage="Confirmar Senha"
                    />
                  </Box>
                  {error && <p>{error}</p>}
                  <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                    Redefinir Senha
                  </Button>
                </>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  onClick={() => navigate('/signin')}
                >
                  Fazer login
                </Button>
              )}
            </Box>
          </Box>
        </Card>
      </PasswordContainer>
      {alert && (
        <WarningAlert message={alertMessage} />
      )}
    </>
  );
}

export default function ResetPassword(props: { disableCustomTheme?: boolean }) {
  return (
    <AppTheme {...props}>
      <PasswordContent />
    </AppTheme>
  )
}